import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SellerPermission, StoreRoleView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import {
  invalidateSellerMembers,
  invalidateSellerRoles,
} from "@/lib/query/core/invalidation";

type UpdateStoreRoleInput = {
  storeSlug: string;
  roleId: string;
  name?: string;
  description?: string | null;
  permissions?: SellerPermission[];
};

export function useUpdateStoreRoleMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: ({
      storeSlug: _storeSlug,
      roleId,
      ...body
    }: UpdateStoreRoleInput) =>
      apiJsonClient<StoreRoleView>(endpoints.seller.roleById(roleId), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidateSellerRoles(queryClient, variables.storeSlug),
        // Member dropdowns show role names; invalidate so a rename
        // propagates without a hard refresh.
        invalidateSellerMembers(queryClient, variables.storeSlug),
      ]);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
